import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Shield,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  XCircle,
  CheckCircle2,
  BarChart3,
  CreditCard,
  Zap,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { User } from '@/api/entities';
import { getApiUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { toast } from 'sonner';

const RISK_LEVEL_COLORS = {
  normal: 'text-emerald-400',
  elevated: 'text-brand-400',
  highest: 'text-red-400',
  high: 'text-red-400',
  not_assessed: 'text-white/50',
  unknown: 'text-white/50',
};

const RISK_LEVEL_LABELS = {
  normal: 'Normal',
  elevated: 'Elevated',
  highest: 'Highest',
  high: 'High',
  not_assessed: 'Not assessed',
  unknown: 'Unknown',
};

const OUTCOME_LABELS = {
  authorized: 'Authorized',
  issuer_declined: 'Issuer declined',
  blocked: 'Blocked (Radar)',
  manual_review: 'Manual review',
  invalid: 'Invalid',
  unknown: 'Unknown',
};

const FAILURE_CODE_LABELS = {
  card_declined: 'Card declined',
  insufficient_funds: 'Insufficient funds',
  expired_card: 'Expired card',
  incorrect_cvc: 'Incorrect CVC',
  processing_error: 'Processing error',
  stolen_card: 'Stolen card',
  lost_card: 'Lost card',
  do_not_honor: 'Do not honor',
  generic_decline: 'Generic decline',
  unknown: 'Unknown',
};

function formatAmount(amountCents, currency = 'usd') {
  const num = (amountCents || 0) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format(num);
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts * 1000);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function RiskInsights() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [charges, setCharges] = useState([]);
  const [stats, setStats] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [daysFilter, setDaysFilter] = useState(30);
  const [deferaZeroAudit, setDeferaZeroAudit] = useState([]);
  const [showDeferaZeroAudit, setShowDeferaZeroAudit] = useState(false);
  const [loadingDeferaZeroAudit, setLoadingDeferaZeroAudit] = useState(false);

  const canAccess = !!currentUser && (currentUser.role === 'admin' || currentUser.app_metadata?.roles?.includes('admin'));

  const load = async () => {
    if (!canAccess) return;
    try {
      setIsRefreshing(true);
      setError(null);
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const createdGte = new Date();
      createdGte.setDate(createdGte.getDate() - daysFilter);
      const createdGteSec = Math.floor(createdGte.getTime() / 1000);

      const resp = await fetch(getApiUrl('admin/radar/risk-insights'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        }),
        credentials: 'omit',
        body: JSON.stringify({ limit: 100, created_gte: createdGteSec }),
      });

      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || `HTTP ${resp.status}`);
      }

      const data = await resp.json();
      setCharges(Array.isArray(data.charges) ? data.charges : []);
      setStats(data.stats || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      toast.error('Failed to load risk insights');
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadDeferaZeroAudit = async () => {
    if (!canAccess) return;
    setLoadingDeferaZeroAudit(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const resp = await fetch('/api/admin/defera-zero-initial-audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        credentials: 'omit',
        body: JSON.stringify({ limit: 30 }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setDeferaZeroAudit(data.data || []);
      }
    } finally {
      setLoadingDeferaZeroAudit(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const me = await User.me();
        setCurrentUser(me);
      } catch {
        // ignore
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (canAccess) load();
  }, [canAccess, daysFilter]); // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showDeferaZeroAudit && canAccess) loadDeferaZeroAudit();
  }, [showDeferaZeroAudit, canAccess]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const elevatedCount = charges.filter((c) => {
    const level = (c.outcome?.risk_level || '').toLowerCase();
    return level === 'elevated' || level === 'highest' || level === 'high';
  }).length;

  const renderChargeRow = (c) => {
    const level = (c.outcome?.risk_level || 'unknown').toLowerCase();
    const colorClass = RISK_LEVEL_COLORS[level] || RISK_LEVEL_COLORS.unknown;
    const label = RISK_LEVEL_LABELS[level] || level;
    const statusColor =
      c.status === 'succeeded'
        ? 'text-emerald-400'
        : c.status === 'failed'
          ? 'text-red-400'
          : 'text-white/60';
    return { level, colorClass, label, statusColor };
  };

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <p className="text-white/60 font-normal">Loading…</p>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-white/85 font-normal">Administrator access required.</p>
        </div>
      </div>
    );
  }

  const s = stats || {};
  const outcomeEntries = Object.entries(s.outcome_breakdown || {}).sort((a, b) => b[1] - a[1]);
  const failureEntries = Object.entries(s.failure_code_breakdown || {}).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <Helmet>
        <title>Risk Insights - Defera</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-4 sm:py-6">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-light text-white flex items-center gap-2 sm:gap-3">
            <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-brand-400 shrink-0" />
            Risk Insights
          </h2>
          <p className="text-white/60 text-[13px] sm:text-sm font-normal mt-1">
            Stripe Radar risk scores, success rates, and decline analysis for charges in the selected period.
          </p>
        </motion.div>

        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="flex gap-2 items-center">
            <span className="text-white/60 text-sm shrink-0">Period:</span>
            <select
              value={daysFilter}
              onChange={(e) => setDaysFilter(Number(e.target.value))}
              className="glass text-white border-white/20 rounded-lg px-3 py-2 text-sm bg-transparent flex-1 sm:flex-initial min-w-0"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <Button
            onClick={load}
            disabled={isRefreshing}
            className="bg-white/10 hover:bg-white/20 text-white border border-white/20 w-full sm:w-auto"
          >
            <RefreshCw className={`w-4 h-4 mr-2 shrink-0 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>

        {error && (
          <div className="mb-4 sm:mb-6 flex items-center gap-2 text-brand-400 bg-brand-500/10 border border-brand-500/30 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-sm">
            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
            <span className="min-w-0 break-words">{error}</span>
          </div>
        )}

        {/* Primary stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <Card className="glass-light border-white/10">
            <CardContent className="pt-4 sm:pt-5 pb-4 sm:pb-5 px-3 sm:px-4">
              <div className="flex items-center gap-2 text-white/60 text-xs sm:text-sm">
                <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                Total
              </div>
              <p className="text-xl sm:text-2xl font-light text-white mt-1">{s.total ?? charges.length}</p>
            </CardContent>
          </Card>
          <Card className="glass-light border-white/10">
            <CardContent className="pt-4 sm:pt-5 pb-4 sm:pb-5 px-3 sm:px-4">
              <div className="flex items-center gap-2 text-emerald-400/80 text-xs sm:text-sm">
                <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                Succeeded
              </div>
              <p className="text-xl sm:text-2xl font-light text-white mt-1">{s.succeeded ?? 0}</p>
            </CardContent>
          </Card>
          <Card className="glass-light border-white/10">
            <CardContent className="pt-4 sm:pt-5 pb-4 sm:pb-5 px-3 sm:px-4">
              <div className="flex items-center gap-2 text-red-400/80 text-xs sm:text-sm">
                <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                Declined
              </div>
              <p className="text-xl sm:text-2xl font-light text-white mt-1">{s.failed ?? 0}</p>
            </CardContent>
          </Card>
          <Card className="glass-light border-white/10">
            <CardContent className="pt-4 sm:pt-5 pb-4 sm:pb-5 px-3 sm:px-4">
              <div className="flex items-center gap-2 text-white/60 text-xs sm:text-sm">
                <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                Success rate
              </div>
              <p className="text-xl sm:text-2xl font-light text-white mt-1">
                {s.success_rate_pct != null ? `${s.success_rate_pct}%` : '—'}
              </p>
            </CardContent>
          </Card>
          <Card className="glass-light border-white/10 col-span-2 sm:col-span-1">
            <CardContent className="pt-4 sm:pt-5 pb-4 sm:pb-5 px-3 sm:px-4">
              <div className="flex items-center gap-2 text-brand-400/80 text-xs sm:text-sm">
                <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                Elevated risk
              </div>
              <p className="text-xl sm:text-2xl font-light text-white mt-1">{elevatedCount}</p>
            </CardContent>
          </Card>
        </div>

        {/* Defera Zero Initial Audit (collapsible) */}
        <Card className="glass-light border-white/10 mb-4 sm:mb-6">
          <button
            type="button"
            onClick={() => setShowDeferaZeroAudit(!showDeferaZeroAudit)}
            className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
          >
            <CardTitle className="text-white font-normal text-sm sm:text-base flex items-center gap-2">
              <Zap className="w-4 h-4 text-brand-400" />
              Defera Zero Initial (Radar-targeted)
            </CardTitle>
            {showDeferaZeroAudit ? (
              <ChevronUp className="w-4 h-4 text-white/60" />
            ) : (
              <ChevronDown className="w-4 h-4 text-white/60" />
            )}
          </button>
          {showDeferaZeroAudit && (
            <CardContent className="px-4 sm:px-6 pb-4 pt-0">
              {loadingDeferaZeroAudit ? (
                <p className="text-white/50 text-sm">Loading…</p>
              ) : deferaZeroAudit.length === 0 ? (
                <p className="text-white/65 text-sm">No initial Zero events yet.</p>
              ) : (
                <div className="space-y-2 text-sm">
                  {deferaZeroAudit.map((row) => (
                    <div
                      key={row.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-white/5 last:border-0"
                    >
                      <span className="text-white/40 font-mono text-xs">
                        {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                      </span>
                      <span className="text-white/70">{row.stripe_object_type}</span>
                      <span className="text-white/50 font-mono text-xs truncate max-w-[120px]" title={row.stripe_object_id}>
                        {row.stripe_object_id}
                      </span>
                      <span className="text-white/60 text-xs">{row.defera_user_id || '—'}</span>
                      <span className="text-white/60 text-xs">{row.defera_booking_id || '—'}</span>
                      <span
                        className={
                          row.status === 'succeeded'
                            ? 'text-emerald-400'
                            : row.status === 'requires_payment_method'
                              ? 'text-brand-400'
                              : 'text-white/60'
                        }
                      >
                        {row.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Outcome & failure breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 sm:mb-6">
          <Card className="glass-light border-white/10">
            <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
              <CardTitle className="text-white font-normal text-sm sm:text-base flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Outcome breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
              {outcomeEntries.length === 0 ? (
                <p className="text-white/65 text-sm">No outcome data for this period.</p>
              ) : (
                <div className="space-y-2">
                  {outcomeEntries.map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between gap-4">
                      <span className="text-white/85 text-sm">
                        {OUTCOME_LABELS[type] || type.replace(/_/g, ' ')}
                      </span>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="h-1.5 flex-1 min-w-[60px] max-w-[100px] bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-white/30 rounded-full"
                            style={{
                              width: `${s.total ? (count / s.total) * 100 : 0}%`,
                            }}
                          />
                        </div>
                        <span className="text-white/85 text-sm font-mono w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="glass-light border-white/10">
            <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
              <CardTitle className="text-white font-normal text-sm sm:text-base flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-400/80" />
                Top decline reasons
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
              {failureEntries.length === 0 ? (
                <p className="text-white/65 text-sm">No declines in this period.</p>
              ) : (
                <div className="space-y-2">
                  {failureEntries.slice(0, 6).map(([code, count]) => (
                    <div key={code} className="flex items-center justify-between gap-4">
                      <span className="text-white/85 text-sm truncate">
                        {FAILURE_CODE_LABELS[code] || code.replace(/_/g, ' ')}
                      </span>
                      <span className="text-red-400/80 text-sm font-mono shrink-0">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charges table */}
        <Card className="glass-light border-white/10 overflow-hidden">
          <CardHeader className="px-4 sm:px-6">
            <CardTitle className="text-white font-light text-base sm:text-lg">Charges ({charges.length})</CardTitle>
          </CardHeader>
          <CardContent className="px-0 sm:px-6 pb-4 sm:pb-6">
            {charges.length === 0 ? (
              <p className="text-white/60 font-normal px-4 sm:px-0">No charges found for the selected period.</p>
            ) : (
              <>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-white/60 border-b border-white/10">
                        <th className="pb-3 pr-4 font-medium">Date</th>
                        <th className="pb-3 pr-4 font-medium">Amount</th>
                        <th className="pb-3 pr-4 font-medium">Customer</th>
                        <th className="pb-3 pr-4 font-medium">Risk</th>
                        <th className="pb-3 pr-4 font-medium">Score</th>
                        <th className="pb-3 pr-4 font-medium">Status</th>
                        <th className="pb-3 pr-4 font-medium">Failure</th>
                      </tr>
                    </thead>
                    <tbody>
                      {charges.map((c) => {
                        const { colorClass, label, statusColor } = renderChargeRow(c);
                        const failureLabel = c.failure_code
                          ? (FAILURE_CODE_LABELS[c.failure_code] || c.failure_code)
                          : '—';
                        return (
                          <tr key={c.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 pr-4 text-white/80 font-mono text-xs">
                              {formatDate(c.created)}
                            </td>
                            <td className="py-3 pr-4 text-white/90">
                              {formatAmount(c.amount, c.currency)}
                            </td>
                            <td className="py-3 pr-4 text-white/85 max-w-[140px] truncate" title={c.customer_email || c.customer_id || ''}>
                              {c.customer_email || c.customer_id || '—'}
                            </td>
                            <td className={`py-3 pr-4 ${colorClass}`}>{label}</td>
                            <td className="py-3 pr-4 text-white/85">
                              {c.outcome?.risk_score != null ? c.outcome.risk_score : '—'}
                            </td>
                            <td className={`py-3 pr-4 ${statusColor}`}>{c.status}</td>
                            <td className="py-3 pr-4 text-red-400/80 text-xs max-w-[100px] truncate" title={c.failure_message || ''}>
                              {failureLabel}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="md:hidden divide-y divide-white/10">
                  {charges.map((c) => {
                    const { colorClass, label, statusColor } = renderChargeRow(c);
                    const customerDisplay = c.customer_email || c.customer_id || '—';
                    const failureLabel = c.failure_code
                      ? (FAILURE_CODE_LABELS[c.failure_code] || c.failure_code)
                      : null;
                    return (
                      <div key={c.id} className="px-4 py-3 active:bg-white/5 transition-colors">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-medium text-sm">
                              {formatAmount(c.amount, c.currency)}
                            </p>
                            <p className="text-white/60 text-xs mt-0.5 truncate" title={customerDisplay}>
                              {customerDisplay}
                            </p>
                            <p className="text-white/40 font-mono text-[11px] mt-1">
                              {formatDate(c.created)}
                            </p>
                            {failureLabel && (
                              <p className="text-red-400/80 text-[11px] mt-1">{failureLabel}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className={`text-xs font-medium ${colorClass}`}>{label}</span>
                            {c.outcome?.risk_score != null && (
                              <span className="text-white/60 text-xs">Score: {c.outcome.risk_score}</span>
                            )}
                            <span className={`text-xs ${statusColor}`}>{c.status}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
